import React, { useEffect, useState } from "react";

import { useGetRecentData } from "api/api-hooks/datasets-hooks";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import {
  DatasetDataInfo,
  mapDatasetDataInfoResponseToDatasetDataInfo,
} from "models/dataset-models";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import useDatasetIdentifiersStore from "store/dataset-identifiers-store";
import DatasetLineChart from "../charts/dataset-line-chart/DatasetLineChart";
import DatasetSetpointRow from "./DatasetSetpointRow";
import "./DatasetSetpointTable.scss";
import {
  IDatasetSetpointConfig,
  IDatasetSetpointRowConfig,
} from "./common/api/interfaces/interfaces";

const DatasetSetpointTable = (props: IDatasetSetpointConfig) => {
  const [data, setData] = useState<Array<IDatasetSetpointRowConfig>>();
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [configChecked, setConfigChecked] = useState<boolean>(false);
  const [dialog, setDialog] = useState<boolean>(false);
  const [uniqueArr, setUniqueArr] = useState<Array<string>>([]);

  const selectedIdsStore = useDatasetIdentifiersStore();
  const { ids: selectedIds } = selectedIdsStore;

  useEffect(() => {
    concatArrays(tempArr => {
      const uniqueArray = [...new Set(tempArr)];
      setUniqueArr(uniqueArray);
    });
  }, [props.values]);

  const { data: recentData } = useGetRecentData(uniqueArr, {
    count: 1,
    options: {
      refetchInterval: props.updateFrequency ? props.updateFrequency * 1000 : 0,
    },
  });

  useEffect(() => {
    if (configChecked) {
      return;
    }

    if (Object.entries(props).length === 0) {
      setError("No config have been provided");
      setLoading(false);
    } else if (!props.values || props.values.length === 0) {
      setError("Value array is empty or field is not provided");
      setLoading(false);
    }

    setConfigChecked(true);
  }, [loading, props]);

  useEffect(() => {
    if (recentData) {
      remakeObjectArray(recentData);
    }
  }, [recentData]);

  const cancelSelections = (e: React.MouseEvent) => {
    if (e.ctrlKey || e.detail === 2) {
      return;
    }
    selectedIdsStore.clear();;
  };

  const openDialog = () => {
    if (!dialog) {
      setDialog(true);
    }
  };

  const concatArrays = (callback: (tempArr: Array<string>) => void) => {
    const tempArr: Array<string> = [];

    if (props.showSetpointColumn) {
      props.values.forEach(ob => {
        if (ob.dataset) {
          tempArr.push(ob.dataset);
        }
        if (ob.setpointDataset) {
          tempArr.push(ob.setpointDataset);
        }
      });
    } else {
      props.values.forEach(ob => {
        if (ob.dataset) {
          tempArr.push(ob.dataset);
        }
      });
    }
    callback(tempArr);
  };

  const remakeObjectArray = (res: Array<DatasetDataInfoResponse>) => {
    const tempArr: Array<IDatasetSetpointRowConfig> = [];
    const tempValues = [...props.values];

    tempValues.forEach(ob => {
      const tempObj: IDatasetSetpointRowConfig = {
        tag: ob.tag,
        name: ob.name,
        opened: false,
        setpointSelected: false,
        datasetSelected: false,
        setIdentifiers() {},
      };

      if (ob.dataset) {
        const tempDatasetObject = res.find(e => e.identifier === ob.dataset);
        if (tempDatasetObject) {
          tempObj.dataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
          tempObj.highlightColor = ob.highlightColor;
        }
      }
      if (ob.setpointDataset && props.showSetpointColumn) {
        const tempDatasetObject = res.find(e => e.identifier === ob.setpointDataset);
        if (tempDatasetObject) {
          tempObj.setpointDataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
          tempObj.validation = ob.validation;
          tempObj.highlightColor = ob.highlightColor;
        }
      }
      tempObj.highlightColor = ob.highlightColor;
      tempArr.push(tempObj);
    });
    setData(tempArr);

    if (loading) {
      setLoading(false);
    }
  };

  const setIdentifier = (id: string, ctrlPressed: boolean, doubleClick: boolean) => {
    switch (true) {
      case ctrlPressed && doubleClick:
        selectedIdsStore.addIdentifier(id);
        openDialog();
        break;
      case ctrlPressed && !doubleClick:
        if (!selectedIds.has(id)) {
          selectedIdsStore.addIdentifier(id);
          break;
        } 
        selectedIdsStore.removeIdentifier(id);
        break;
      case !ctrlPressed && doubleClick:
        selectedIdsStore.addIdentifier(id);
        openDialog();
        break;
      default:
        break;
    }

  };

  const printableCheck = (
    tag: string | undefined,
    name: string | undefined,
    dataset: DatasetDataInfo | undefined,
    setpointDataset: DatasetDataInfo | undefined
  ) => {
    if (!name && !dataset) {
      if (!props.showSetpointColumn && !props.showTagColumn) {
        return false;
      }
      if (!props.showSetpointColumn && props.showTagColumn && !tag) {
        return false;
      }
      if (props.showSetpointColumn && !props.showTagColumn && !setpointDataset) {
        return false;
      }
    }
    return true;
  };

  if (loading) {
    return (
      <ProgressSpinner
        style={{
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      />
    );
  }

  if (error.length > 0) {
    return <div className="main-window">{error}</div>;
  }

  return (
    <div className="main-main-window" >
      {props && props.values.length > 0 && (
        <>
          <Dialog
            header="SCADA tags chart"
            visible={dialog}
            style={{ width: "min(100vw, 1000px)", height: "500px" }}
            modal={false}
            onHide={() => {
              setDialog(false);
            }}
          >
            <DatasetLineChart
              identifiers={[...selectedIds]}
              showDateSelection
              dynamicHeight
              chartDialogOpen={dialog}
            />
          </Dialog>

          <div  onClick={cancelSelections}>
            <table className="main-window">
              <tbody>
                {props.showTableTitles && (
                  <tr id="table-head">
                    {props.showTagColumn === true && <th>{props.tableTitles?.tagTitle}</th>}
                    <th>{props.tableTitles?.datasetTitle}</th>
                    <th>{props.tableTitles?.datasetValueTitle}</th>
                    {props.showSetpointColumn === true && (
                      <th>{props.tableTitles?.setpointValueTitle}</th>
                    )}
                  </tr>
                )}
                {data?.map(
                  (
                    value: IDatasetSetpointRowConfig,
                    index: number,
                    array: IDatasetSetpointRowConfig[]
                  ) => {
                    const nameBeforeWasEmpty = index >= 1 ? array[index - 1]?.name === " " : false;
                    if (printableCheck(value.tag, value.name, value.dataset, value.setpointDataset)) {
                      return (
                        <DatasetSetpointRow
                          showSetpointColumn={props.showSetpointColumn}
                          showTagColumn={props.showTagColumn}
                          compactMode={props.compactMode ? props.compactMode : false}
                          tag={value.tag}
                          name={value.name}
                          nameBeforeWasEmpty={nameBeforeWasEmpty}
                          setpointDataset={value.setpointDataset}
                          dataset={value.dataset}
                          datasetSelected={selectedIds.has(
                            value.dataset ? value.dataset.identifier : ""
                          )}
                          setpointSelected={selectedIds.has(
                            value.setpointDataset ? value.setpointDataset.identifier : ""
                          )}
                          opened={dialog}
                          setIdentifiers={setIdentifier}
                          validation={value.validation}
                          highlightColor={value.highlightColor}
                          key={value.dataset?.identifier ?? index}
                        />
                      );
                    }
                    return null;
                  }
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default DatasetSetpointTable;
